<template>
  <div class="tw-relative w-100">
    <BDropdown
      :id="props.id"
      v-model="isDropdownOpen"
      :class="[
        'tw-relative baseMultiDropDown',
        'focusDrop',
        className,
        validation.$error ? 'invalid' : '',
      ]"
      block
      menu-class="w-100"
      @keydown.down="moveSelectionDown(1)"
      @keydown.up="moveSelectionUp(-1)"
      @keydown="selectItem"
      @show="showDropdow"
    >
      <slot>
        <BaseDropDownItem
          v-for="(option, index) in props.data"
          :id="option.id.toString()"
          :key="option.id"
          :selected="modelValue.some((item) => item === option.id)"
          :text="option.name"
          :class-name="`normal-dropdown-container ${
            activeItem === index ? 'activeItem' : ''
          }`"
          @click="handleSelect(option.id)"
        />
      </slot>
      <template #button-content>
        <slot name="button-content">
          <div
            class="role-container tw-flex tw-justify-between tw-items-center"
          >
            <div class="role-inner tw-flex tw-gap-[5px]">
              <span
                v-if="selectedOptions.length === 0"
                class="placeholder-container"
                >{{ placeholder }}</span
              >
              <div
                v-for="item in props.data.filter((item) =>
                  selectedOptions.includes(item.id),
                )"
                :key="item.id"
                class="role tw-text-sm tw-font-medium tw-flex tw-items-center tw-px-[13px] tw-text-[#231F20]"
              >
                <span>{{ item.name }}</span>
              </div>
            </div>
            <ArrowIcon class="right-arrow" :width="6.661" :height="10" />
          </div>
        </slot>
      </template>
    </BDropdown>
  </div>
</template>
<script lang="ts" setup>
import { PropType, ref, Ref, watch } from "vue";
import BaseDropDownItem from "@/components/common/form/baseDropDownItem.vue";
import { IDropDownData } from "@/interfaces/common/IDropDown";
import ArrowIcon from "@/components/svgs/arrowIcon.vue";
import store from "@/store";
import { OVERLAY_KEYBOARD } from "@/utils/constants";
const isDropdownOpen = ref(false);
const props = defineProps({
  modelValue: {
    type: Array as PropType<(string | number)[]>,
    required: true,
  },
  id: {
    type: String,
    required: true,
  },
  className: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  data: {
    type: Array as PropType<IDropDownData[]>,
    required: true,
  },
  options: {
    type: Array as PropType<IDropDownData[]>,
    default: () => [{}],
  },
  description: {
    type: String,
    default: "",
  },
  validation: {
    type: Object,
    default: () => ({}),
  },
});

const activeItem = ref(0);
const emit = defineEmits(["update:modelValue"]);
const selectedOptions: Ref<(string | number)[]> = ref(props.modelValue || []);
const showDropdow = () => {
  store.commit("keyBoard/SET_ENTER", OVERLAY_KEYBOARD.DROPDOWN); //
};
const handleSelect = (val: string | number) => {
  if (selectedOptions.value.includes(val)) {
    selectedOptions.value = selectedOptions.value.filter(
      (item) => item.toString() !== val.toString(),
    );
  } else {
    selectedOptions.value.push(val);
  }
  setTimeout(() => {
    store.commit("keyBoard/SET_ENTER", OVERLAY_KEYBOARD.MODAL);
  }, 1000);
  emit("update:modelValue", selectedOptions.value);
};

const selectItem = (event) => {
  if (event.key == "ArrowDown") {
    isDropdownOpen.value = true;
    store.commit("keyBoard/SET_ENTER", OVERLAY_KEYBOARD.DROPDOWN);
  }
  if (event.key == "Enter") {
    let id: any = props.data[activeItem.value].id;
    handleSelect(id);
    isDropdownOpen.value = false; // close dropdow when select value
    setTimeout(() => {
      store.commit("keyBoard/SET_ENTER", OVERLAY_KEYBOARD.MODAL);
    }, 1000);
  }
};

const moveSelectionDown = (step) => {
  if (props.data.length - 1 == activeItem.value) {
    activeItem.value = props.data.length - 1;
  } else if (activeItem.value < props.data.length) {
    activeItem.value += step;
    const elements = document.getElementsByClassName("show");
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i] as HTMLElement;
      element.scrollTop += 61;
    }
  }
};
const moveSelectionUp = (step) => {
  if (activeItem.value >= 1) {
    activeItem.value += step;
    const elements = document.getElementsByClassName("show");
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i] as HTMLElement;
      element.scrollTop -= 61;
    }
  }
};
watch(isDropdownOpen, (newValue, oldValue) => {
  if (!newValue && oldValue) {
    setTimeout(() => {
      store.commit("keyBoard/SET_ENTER", OVERLAY_KEYBOARD.MODAL);
    }, 1000);
  }
});
</script>
<style lang="scss">
.full-width-dropdown {
  width: calc(100% - 30px) !important;
}
.activeItem {
  background-color: #eff0f1 !important;
  border: 1px solid #eff0f1;
}
.baseMultiDropDown {
  &.invalid {
    .btn-secondary {
      background-color: #fee5e2 !important;
      border-color: #f7553c !important;
    }
  }

  .btn-secondary {
    width: 100%;
    text-align: left;
    line-height: 100%;
    padding: 11px 20px;
    color: #231f20;
    font-size: 13px;
    background: transparent;
    border: none;
    box-shadow: none;

    .placeholder {
      color: #b7b7b7;
      background-color: transparent !important;
      opacity: 1;
    }
    &:hover,
    &:focus &:active,
    &:active:focus {
      color: #b7b7b7;
      font-size: 13px;
      background: transparent;
      border: none;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
    }

    &::after {
      display: none;
    }
  }

  .form-control {
    border-radius: 6px;
    border: 1px solid var(--ui-rule, #eff0f1);
    background: var(--ui-rule-50, rgba(239, 240, 241, 0.5));
    padding: 0;
    height: 58px;
    color: #231f20;
    font-size: 13px;
    font-family: Poppins;
    font-style: normal;
    font-weight: 400;
    line-height: normal;

    &::placeholder {
      color: #b7b7b7;
    }
  }

  input.form-control {
    border-radius: 6px;
    border: 1px solid var(--ui-rule, #eff0f1);
    background: var(--ui-rule-50, rgba(239, 240, 241, 0.5));
    padding: 18px 20px;
    height: 58px;
    color: #231f20;
    font-size: 13px;
    font-family: Poppins;
    font-style: normal;
    font-weight: 400;
    line-height: normal;

    &::placeholder {
      color: #b7b7b7;
    }
  }

  .dropdown-toggle.show .right-arrow {
    transition: transform 0.3s ease;
    transform: rotate(-90deg);
    height: 12px;
  }

  .dropdown-toggle .right-arrow {
    transition: transform 0.3s ease;
    transform: rotate(90deg);
    height: 12px;
  }

  .normal-dropdown-container {
    width: 100%;

    .dropdown-toggle::after {
      display: none;
    }
  }

  &.gray-dropdown-container {
    width: 100%;

    .btn-secondary {
      width: 100%;
      text-align: left;
      line-height: 100%;
      padding: 11px 20px;

      color: #b7b7b7;
      font-size: 13px;

      background-color: #eff0f180;
      border: 1px solid #eff0f1;
      box-shadow: none;
      &:hover,
      &:focus,
      &:active,
      &:active:focus {
        color: #b7b7b7;
        font-size: 13px;
        background-color: #eff0f180;
        border: 1px solid #eff0f1;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
      }
    }

    .dropdown-toggle::after {
      display: none;
    }
    &.tw-text-normal {
      .btn {
        color: #231f20;
      }
    }
  }
}

.custom-actions-popover .popover-arrow {
  width: 14px;
  height: 14px;
}

.custom-actions-popover.bs-popover-bottom {
  margin-top: unset;

  .popover-arrow {
    top: -8px;
  }
}

.custom-actions-popover.bs-popover-top {
  .popover-arrow {
    bottom: -8px;
  }
}

.popover {
  border: none;
  box-shadow: 0px 0px 25px 0px rgba(177, 176, 176, 0.35);
  border-radius: 6px;
}

.popover-body {
  padding: 0;
  min-width: 160px;
}
</style>
